import { AlertBanner, Button } from '@curri/components'
import { useEffect, useState, type ReactNode } from 'react'

import type { ApiResult } from '../../api/client.ts'
import { WEBHOOK_WRITE_SCOPE, type WebhookVariant } from '../../api/webhooks.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'
import { useGhost } from '../../ghost/GhostProvider.tsx'
import { describeFailure } from '../../routes/webhooks/format.ts'
import { useWebhookConfig } from '../../routes/webhooks/useWebhookData.ts'
import { navigateOnPlainClick } from '../../shell/links.ts'

// The parts both docs explorers are built from — the delivery lifecycle one on
// /docs/webhooks/delivery-statuses and the payload events one on
// /docs/webhooks/payloads/events.
//
// The two pages differ in what they list and what they send. Everything around
// that is the same bargain: pick a moment on the left, read the exact body Curri
// would POST on the right, and — signed in, with a sandbox endpoint configured —
// really send it and see what the endpoint answered. That shell lives here so a
// fix to the authorization gates or the outcome rendering reaches both pages.
//
// What deliberately stays in the callers: the list itself, its layout (the
// delivery flow draws connectors between statuses; the events are a plain
// column), the right-hand content, and the example data each one mirrors from
// the service.

export const monoLabel = {
  fontFamily: 'var(--curri-font-mono)',
  fontSize: 12.5,
  fontWeight: 600,
} as const

export const Note = ({ children }: { children: ReactNode }) => (
  <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--curri-text-secondary)' }}>
    {children}
  </p>
)

/** Small uppercase heading inside an explorer column. */
export const SectionLabel = ({
  children,
  topGap = 4,
}: {
  children: ReactNode
  topGap?: number
}) => (
  <p
    style={{
      margin: `${topGap}px 0 6px`,
      fontSize: 11.5,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: 'var(--curri-text-tertiary)',
    }}
  >
    {children}
  </p>
)

/** One entry in the left-hand picker. */
export const ExplorerOption = ({
  label,
  description,
  active,
  onSelect,
}: {
  label: string
  description: string
  active: boolean
  onSelect: () => void
}) => (
  <button
    aria-pressed={active}
    onClick={onSelect}
    type="button"
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '9px 12px',
      border: `1px solid ${active ? 'var(--curri-brand-fill)' : 'var(--curri-border)'}`,
      borderRadius: 10,
      background: active ? 'var(--curri-brand-subtle)' : 'var(--curri-surface)',
      cursor: 'pointer',
      transition: 'border-color 120ms var(--curri-ease), background 120ms var(--curri-ease)',
    }}
  >
    <span style={{ ...monoLabel, display: 'block', color: 'var(--curri-text)' }}>
      {label}
    </span>
    <span
      style={{
        display: 'block',
        marginTop: 2,
        fontSize: 12,
        lineHeight: 1.45,
        color: 'var(--curri-text-tertiary)',
      }}
    >
      {description}
    </span>
  </button>
)

/** Picker on the left, the selected moment on the right. */
export const ExplorerLayout = ({
  navLabel,
  nav,
  children,
}: {
  navLabel: string
  nav: ReactNode
  children: ReactNode
}) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, margin: '8px 0 16px' }}>
    <nav aria-label={navLabel} style={{ flex: '1 1 216px', maxWidth: 280 }}>
      {nav}
    </nav>
    <div
      style={{
        flex: '99 1 320px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {children}
    </div>
  </div>
)

/**
 * What both test-send routes report back. `statusText` is the reason phrase,
 * which only the delivery route records.
 */
export interface TestSendOutcome {
  success: boolean
  httpStatus: number | null
  durationMs: number
  responseBody: string | null
  transportError: string | null
  statusText?: string | null
}

const ResponsePreview = ({ body }: { body: string }) => (
  <pre
    style={{
      margin: '6px 0 0',
      padding: '8px 10px',
      maxHeight: 140,
      overflow: 'auto',
      fontFamily: 'var(--curri-font-mono)',
      fontSize: 12,
      lineHeight: 1.5,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      background: 'var(--curri-canvas)',
      border: '1px solid var(--curri-border)',
      borderRadius: 8,
      color: 'var(--curri-text-secondary)',
    }}
  >
    {body}
  </pre>
)

/** What the endpoint answered, styled by outcome rather than thrown away. */
const SendOutcome = <T extends TestSendOutcome>({
  result,
  footerFor,
}: {
  result: ApiResult<T>
  footerFor?: (sent: T) => ReactNode
}) => {
  if (!result.ok) {
    const copy = describeFailure(result.failure)
    return (
      <AlertBanner title={copy.title} type={copy.tone}>
        {copy.body}
      </AlertBanner>
    )
  }

  const sent = result.data
  const headline = sent.transportError
    ? sent.transportError
    : `HTTP ${sent.httpStatus}${sent.statusText ? ` ${sent.statusText}` : ''} in ${sent.durationMs}ms`

  return (
    <div style={{ fontSize: 13 }}>
      <span
        style={{
          ...monoLabel,
          color: sent.success
            ? 'var(--color-text-success)'
            : 'var(--color-text-danger)',
        }}
      >
        {sent.success ? 'Delivered' : 'Failed'} — {headline}
      </span>
      {sent.responseBody !== null && <ResponsePreview body={sent.responseBody} />}
      {footerFor?.(sent)}
    </div>
  )
}

/**
 * The nouns the shared copy is written around, so both pages read naturally
 * without either one owning a second copy of the sentences.
 */
export interface ExplorerSendCopy {
  /** What one selection sends: `payload` for deliveries, `event` for payloads. */
  item: string
  /** The operation, in the missing-scope note: `test webhook`, `test event`. */
  operation: string
  /** The endpoint, in the unconfigured note: `sandbox payload endpoint`. */
  endpoint: string
}

type SendState<T> =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; result: ApiResult<T> }

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>

const SendControls = <T extends TestSendOutcome>({
  selection,
  apiFetch,
  copy,
  sandboxVariant,
  setupPath,
  send,
  footerFor,
  onNavigate,
}: {
  selection: string
  apiFetch: ApiFetch
  copy: ExplorerSendCopy
  sandboxVariant: WebhookVariant
  setupPath: string
  send: (apiFetch: ApiFetch) => Promise<ApiResult<T>>
  footerFor?: (sent: T) => ReactNode
  onNavigate?: (path: string) => void
}) => {
  // Config is fetched once for the explorer's lifetime; only the send state
  // resets per selection, so clicking through the list costs no extra reads.
  const { resource } = useWebhookConfig()
  const [state, setState] = useState<SendState<T>>({ kind: 'idle' })
  useEffect(() => {
    setState({ kind: 'idle' })
  }, [selection])

  const sandbox =
    resource.status === 'ready'
      ? resource.data.find(config => config.variant === sandboxVariant)
      : undefined

  if (resource.status === 'ready' && !sandbox) {
    return (
      <Note>
        No {copy.endpoint} is configured yet —{' '}
        <a
          href={setupPath}
          onClick={onNavigate && navigateOnPlainClick(onNavigate, setupPath)}
        >
          set one up first
        </a>{' '}
        and this button comes alive.
      </Note>
    )
  }

  const dispatch = () => {
    if (state.kind === 'sending') return
    setState({ kind: 'sending' })
    void (async () => {
      const result = await send(apiFetch)
      setState({ kind: 'sent', result })
    })()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Button
          disabled={resource.status !== 'ready'}
          label={`Send ${selection} to your sandbox endpoint`}
          loading={state.kind === 'sending'}
          onClick={dispatch}
          size="small"
          type="button"
          variant="secondary"
        />
        {sandbox && (
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11.5,
              color: 'var(--curri-text-tertiary)',
              overflowWrap: 'anywhere',
            }}
          >
            POSTs to {sandbox.url}
          </span>
        )}
      </div>
      {state.kind === 'sent' && (
        <SendOutcome footerFor={footerFor} result={state.result} />
      )}
    </div>
  )
}

/**
 * The send controls under the body, with the gates in front of them. Every
 * state renders *something* — a signed-out reader should see that this page can
 * do more than display JSON.
 */
export const ExplorerSendSection = <T extends TestSendOutcome>({
  selection,
  copy,
  sandboxVariant,
  setupPath,
  send,
  footerFor,
  onNavigate,
}: {
  /** The selected status or event. Also the key that resets the outcome: a
   *  stale "HTTP 200" under a body that was never sent would claim the wrong
   *  thing. */
  selection: string
  copy: ExplorerSendCopy
  sandboxVariant: WebhookVariant
  setupPath: string
  send: (apiFetch: ApiFetch) => Promise<ApiResult<T>>
  footerFor?: (sent: T) => ReactNode
  onNavigate?: (path: string) => void
}) => {
  const { state, signIn, apiFetch } = useAuth()
  const { target: ghostTarget } = useGhost()

  if (state.status !== 'authenticated') {
    const authenticating = state.status === 'authenticating'
    return (
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Note>
          Sign in to send this {copy.item} to your sandbox endpoint and see what
          your server answers.
        </Note>
        <Button
          disabled={authenticating}
          label={authenticating ? 'Signing in…' : 'Sign in'}
          onClick={signIn}
          size="small"
          type="button"
          variant="secondary"
        />
      </div>
    )
  }

  if (!state.caller.grantedScopes.includes(WEBHOOK_WRITE_SCOPE)) {
    return (
      <Note>
        Sending a {copy.operation} needs the <code>{WEBHOOK_WRITE_SCOPE}</code>{' '}
        scope, which your session was issued without. Sign out and back in to
        request it.
      </Note>
    )
  }

  if (ghostTarget !== null) {
    return (
      <Note>
        Test sends are disabled while viewing as a partner — ghost sessions are
        read-only.
      </Note>
    )
  }

  return (
    <SendControls
      apiFetch={apiFetch}
      copy={copy}
      footerFor={footerFor}
      onNavigate={onNavigate}
      sandboxVariant={sandboxVariant}
      selection={selection}
      send={send}
      setupPath={setupPath}
    />
  )
}
